// Eskom PARCC — CES workbook upload matcher (R5 #1).
//
// PURE: takes the parsed workbook (cesUploadParse), the live register and the
// uploader's grant, and produces one draft per workbook row with an explicit
// outcome — the preview table's row model. Matching is by Contract/PO Number
// against referenceNo (contractNumber as a warned fallback); permission is the
// SAME per-transaction rule as manual capture (canEditTransaction); rows whose
// transaction is already reviewed come back as `replace` so the page can
// require explicit opt-in before clobbering an existing capture (the persisted
// writes are replace-all).
//
// The uploaded sheet's own sub-criterion text becomes the captured criterion —
// consultant papers can lag PARCC's refreshed rubric, so remapping onto the
// current template would falsify what was assessed (see cesUploadParse).
import type { Transaction } from '../../data/types';
import { GAP_STATUS_OPTIONS, type CesRoute } from '../../domain/enums';
import { normalizeGapStatus } from '../../domain/normalize';
import { resolveWorkingPaperTemplate } from '../../domain/workingPapers/resolveTemplate';
import { canEditTransaction, type Grant } from '../../lib/permissions';
import { isReviewed } from '../transactions/editTransaction';
import { refKey } from '../import/importParse';
import type { CesCaptureInput, CesCriterionRow } from './buildCesCapture';
import { normHeaderLabel, type EditableHeaderValues } from './cesHeaderFields';
import type { CesUploadParse, CesUploadRow } from './cesUploadParse';
import { templateToCesRoute } from './templateRoute';

export type CesUploadOutcome =
  | 'ready'
  | 'replace'
  | 'not-found'
  | 'not-editable'
  | 'no-content'
  | 'duplicate';

/** One workbook row, matched and readied (or explained) for import. */
export interface CesUploadDraft {
  sheetName: string;
  excelRow: number;
  referenceNo: string;
  outcome: CesUploadOutcome;
  warnings: string[];
  tx?: Transaction;
  draft?: {
    route: CesRoute;
    rows: CesCriterionRow[];
    additionalFindings: string;
    /** Workbook values for the four OpenText header fields (non-blank only). */
    headerEdits: Partial<EditableHeaderValues>;
    /** The GAPS Status cell folded onto the four-state lifecycle (when it folds). */
    gapsStatus?: string;
  };
}

const GAP_LIFECYCLE = new Set<string>(GAP_STATUS_OPTIONS);

/**
 * A capture already exists when the register says so: reviewed (the canonical
 * {@link isReviewed} rule the register/detail use), OR the CES-save stamps are
 * present (`complianceLevelOverride` / `workingPaperNotes` are written by every
 * capture). Such rows demote to `replace` so overwriting is an explicit tick —
 * an UN-reviewed transaction can still hold a live evaluation.
 */
function hasExistingCapture(t: Transaction): boolean {
  return (
    isReviewed(t) ||
    String(t.complianceLevelOverride ?? '').trim() !== '' ||
    String(t.workingPaperNotes ?? '').trim() !== ''
  );
}

/** Normalised compare for locked-header mismatch warnings. */
function softEq(a: string, b: string): boolean {
  const fa = normHeaderLabel(a);
  const fb = normHeaderLabel(b);
  return fa === '' || fb === '' || fa === fb;
}

function matchTransaction(
  row: CesUploadRow,
  byRef: Map<string, Transaction>,
  byContract: Map<string, Transaction[]>,
): { tx?: Transaction; warnings: string[] } {
  const key = refKey(row.referenceNo);
  if (!key) return { tx: undefined, warnings: [`Row ${row.excelRow}: blank Contract/PO Number.`] };
  const direct = byRef.get(key);
  if (direct) return { tx: direct, warnings: [] };
  const viaContract = byContract.get(key) ?? [];
  if (viaContract.length === 1) {
    return {
      tx: viaContract[0],
      warnings: [
        `Row ${row.excelRow}: matched via the contract number onto ${viaContract[0].referenceNo} (no direct reference match).`,
      ],
    };
  }
  if (viaContract.length > 1) {
    return {
      tx: undefined,
      warnings: [
        `Row ${row.excelRow}: "${row.referenceNo}" matches more than one transaction's contract number — not imported.`,
      ],
    };
  }
  return { tx: undefined, warnings: [] };
}

/** Parsed workbook × register × grant → one outcome-tagged draft per row. */
export function matchCesUpload(
  parsed: CesUploadParse,
  transactions: readonly Transaction[],
  grant: Grant | null,
): CesUploadDraft[] {
  const live = transactions.filter((t) => !t.deleted);
  const byRef = new Map<string, Transaction>();
  for (const t of live) if (t.referenceNo) byRef.set(refKey(t.referenceNo), t);
  const byContract = new Map<string, Transaction[]>();
  for (const t of live) {
    const k = refKey(t.contractNumber);
    if (!k) continue;
    const bucket = byContract.get(k);
    if (bucket) bucket.push(t);
    else byContract.set(k, [t]);
  }

  const drafts: CesUploadDraft[] = [];
  for (const sheet of parsed.sheets) {
    const sheetRoute = templateToCesRoute(sheet.mechanismKey ?? sheet.sheetName.trim());
    for (const row of sheet.rows) {
      const draft: CesUploadDraft = {
        sheetName: sheet.sheetName,
        excelRow: row.excelRow,
        referenceNo: row.referenceNo,
        outcome: 'ready',
        warnings: [...row.warnings],
      };
      drafts.push(draft);

      const { tx, warnings } = matchTransaction(row, byRef, byContract);
      draft.warnings.push(...warnings);
      if (!tx) {
        draft.outcome = 'not-found';
        continue;
      }
      draft.tx = tx;

      if (!canEditTransaction(tx, grant)) {
        draft.outcome = 'not-editable';
        continue;
      }

      const hasContent =
        row.entries.some((e) => e.evidence !== '' || e.status !== '') ||
        row.additionalComments.trim() !== '';
      if (!hasContent) {
        draft.outcome = 'no-content';
        continue;
      }

      // The sheet the consultants used vs the transaction's resolved template —
      // the paper is authoritative about HOW it was assessed, so a disagreement
      // warns rather than blocks (the criterion text travels with the capture).
      const template = resolveWorkingPaperTemplate(tx);
      if (template) {
        const templateRoute = templateToCesRoute(template.mechanism);
        if (templateRoute !== sheetRoute) {
          draft.warnings.push(
            `Row ${row.excelRow}: the transaction's working paper resolves to the ${templateRoute} route, but this sheet is ${sheetRoute} — imported as assessed on the sheet.`,
          );
        }
      }

      // Locked-header sanity: the register stays authoritative, mismatches warn.
      const division = row.header['division'] ?? '';
      if (!softEq(division, tx.division ?? '')) {
        draft.warnings.push(
          `Row ${row.excelRow}: Division "${division}" differs from the register's "${tx.division ?? ''}" — the register value is kept.`,
        );
      }
      const supplier = row.header['supplier name vendor number'] ?? '';
      if (!softEq(supplier, tx.supplier ?? '')) {
        draft.warnings.push(
          `Row ${row.excelRow}: Supplier "${supplier}" differs from the register's "${tx.supplier ?? ''}" — the register value is kept.`,
        );
      }

      // GAPS Status folds onto the lifecycle or is dropped with a warning
      // (values like "Possible IE" are compliance levels, not lifecycle states).
      let gapsStatus: string | undefined;
      const rawGaps = row.gapsStatusRaw.trim();
      if (rawGaps !== '') {
        const folded = normalizeGapStatus(rawGaps);
        if (GAP_LIFECYCLE.has(folded)) gapsStatus = folded;
        else {
          draft.warnings.push(
            `Row ${row.excelRow}: GAPS Status "${rawGaps}" is not a lifecycle state (Open / In Progress / Closed / Closed by Self-Declaration) — left unchanged.`,
          );
        }
      }

      const criterionByPair = new Map(sheet.criteria.map((c) => [c.pairIndex, c]));
      const rows: CesCriterionRow[] = row.entries.map((e, i) => {
        const c = criterionByPair.get(e.pairIndex);
        return {
          seq: i + 1,
          stage: c?.stage || sheet.sheetName.trim(),
          criterion: c?.text || `Sub-criterion ${e.pairIndex + 1}`,
          status: e.status,
          finding: e.evidence,
        };
      });

      const headerEdits: Partial<EditableHeaderValues> = {};
      const csd = row.header['csd number']?.trim();
      const pr = row.header['pr number']?.trim();
      const pm = row.header['procurement manager']?.trim();
      const eu = row.header['end user']?.trim();
      if (csd) headerEdits.csdNumber = csd;
      if (pr) headerEdits.prNumber = pr;
      if (pm) headerEdits.procurementManager = pm;
      if (eu) headerEdits.endUser = eu;

      draft.draft = {
        route: sheetRoute,
        rows,
        additionalFindings: row.additionalComments.trim(),
        headerEdits,
        gapsStatus,
      };
      draft.outcome = hasExistingCapture(tx) ? 'replace' : 'ready';
    }
  }

  // In-workbook duplicates resolve LAST-wins (spreadsheet convention, matching
  // the bulk import): earlier occurrences of the same transaction demote to
  // `duplicate` and are never imported.
  const lastByTx = new Map<string, CesUploadDraft>();
  for (const d of drafts) if (d.tx && d.draft) lastByTx.set(d.tx.id, d);
  for (const d of drafts) {
    if (d.tx && d.draft && lastByTx.get(d.tx.id) !== d) {
      d.outcome = 'duplicate';
      d.warnings.push('A later row in the workbook captures this transaction — that row wins.');
    }
  }
  return drafts;
}

/**
 * A matched draft → the exact `buildCesCapture` input a manual save would use:
 * the transaction's own fields carried through, the four OpenText header
 * fields overridden by non-blank workbook cells, and the folded GAPS Status
 * (register value kept when the cell did not fold).
 */
export function draftToCaptureInput(
  draft: CesUploadDraft,
  capturedByEmail: string,
): CesCaptureInput | undefined {
  const { tx, draft: d } = draft;
  if (!tx || !d) return undefined;
  return {
    route: d.route,
    referenceNo: tx.referenceNo,
    rows: d.rows,
    additionalFindings: d.additionalFindings,
    capturedByEmail,
    tx: {
      dataset: tx.dataset,
      division: tx.division,
      supplier: tx.supplier,
      value: tx.value,
      valueDisplay: tx.valueDisplay,
      mechanism: tx.mechanism,
      year: tx.year,
      period: tx.period,
      link: tx.link,
      recordLabel: tx.recordLabel,
      baseNo: tx.baseNo,
      gapsStatus: d.gapsStatus ?? tx.gapsStatus,
      bu: tx.bu,
      briefDescription: tx.briefDescription,
      buyer: tx.buyer,
      contractNumber: tx.contractNumber,
      csdNumber: d.headerEdits.csdNumber ?? tx.csdNumber ?? '',
      prNumber: d.headerEdits.prNumber ?? tx.prNumber ?? '',
      procurementManager: d.headerEdits.procurementManager ?? tx.procurementManager ?? '',
      endUser: d.headerEdits.endUser ?? tx.endUser ?? '',
    },
  };
}
